import { useState, useEffect, useRef, useCallback, lazy, Suspense } from "react";
import { supabase } from "./supabaseClient";
import { AuthProvider, useAuth } from "./AuthContext";
import { NotificationProvider } from "./NotificationContext";
import { NotificationToast } from "./NotificationBell";
import { useUnreadMessages } from "./useUnreadMessages";
import TeamRegister from "./TeamRegister";
import TeamManage from "./TeamManage";
import MatchList from "./MatchList";
import MyMatches from "./MyMatches";
import ChatList from "./ChatList";
import TeamProfile from "./TeamProfile";
import ChatRoom from "./ChatRoom";
import PostCreate from "./PostCreate";
import PostDetail from "./PostDetail";
import Board from "./Board";
import TeamDetail from "./TeamDetail";
import LoginPage from "./LoginPage";
import PrivacyPolicy from "./PrivacyPolicy";
import Support from "./pages/Support";
import TermsAgreement, { hasAgreedToTerms } from "./pages/TermsAgreement";
import NicknameModal from "./components/NicknameModal";
import useNickname from "./hooks/useNickname";
import splashLogo from "./logo_sp.png";
import newSplashLogo from './s_logo.png';
import { SplashScreen as CapSplashScreen } from '@capacitor/splash-screen';
import { openExternalUrl } from "./utils/openExternalUrl";
import JoinPage from "./JoinPage";
import MyMatchEvents from "./MyMatchEvents";
import MatchEventDetail from "./MatchEventDetail";
import ProfileSetupPage from "./ProfileSetupPage";
import MyProfilePage from "./MyProfilePage";
const AdminPage = lazy(() => import("./AdminPage"));
import UserGuide from "./UserGuide";

// 앱 버전 체크
const CURRENT_VERSION = '1.9';

const checkAppUpdate = async () => {
  try {
    const { data, error } = await supabase
      .from('app_config')
      .select('value')
      .eq('key', 'latest_android_version')
      .single();

    if (error || !data) return;

    const latestVersion = data.value;

    if (latestVersion !== CURRENT_VERSION) {
      const shouldUpdate = window.confirm(
        `새로운 버전(v${latestVersion})이 있습니다!\n\n지금 업데이트하시겠습니까?\n\n(내 정보 > 기타 > 버전 정보에서도 업데이트할 수 있어요)`
      );
      if (shouldUpdate) {
        await openExternalUrl('https://play.google.com/apps/testing/com.crewpick.android');
      }
    }
  } catch (e) {
    console.error('버전 체크 실패:', e);
  }
};

const TABS = [
  {
    key: "match",
    label: "교류전 매칭",
    icon: (active) => (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
        stroke={active ? "#E24B4A" : "#aaa"} strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
    ),
  },
  {
    key: "chat",
    label: "채팅",
    icon: (active) => (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
        stroke={active ? "#E24B4A" : "#aaa"} strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
    ),
  },
  {
    key: "mymatches",
    label: "내 매칭",
    icon: (active) => (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
        stroke={active ? "#E24B4A" : "#aaa"} strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="9" y1="13" x2="15" y2="13" />
        <line x1="9" y1="17" x2="13" y2="17" />
      </svg>
    ),
  },
  {
    key: "register",
    label: "내 정보",
    icon: (active) => (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
        stroke={active ? "#E24B4A" : "#aaa"} strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <line x1="19" y1="8" x2="19" y2="14" />
        <line x1="22" y1="11" x2="16" y2="11" />
      </svg>
    ),
  },
];


function SplashScreen({ onDone }) {
  const [showSub, setShowSub] = useState(false);

  useEffect(() => {
    // 네이티브 스플래시 숨기기 (React 인트로가 이어받음 - 흰 화면 깜빡임 방지)
    (async () => {
      try {
        const { Capacitor } = await import('@capacitor/core');
        if (Capacitor.isNativePlatform()) {
          await CapSplashScreen.hide();
        }
      } catch (e) {
        console.error('스플래시 hide 실패:', e);
      }
    })();

    const timers = [
      setTimeout(() => setShowSub(true), 500),   // 0.5초 후 "동호회 교류전 매칭" 페이드인
      setTimeout(onDone, 1000),                  // 1초 후 종료 (성능 개선: 기존 2초 -> 1초)
    ];
    return () => timers.forEach(clearTimeout);
  }, [onDone]);

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: '#000D21',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <img
        src={newSplashLogo}
        alt="CrewPick"
        style={{ width: 300, maxWidth: '80%', height: 'auto' }}
      />
      <p style={{
        marginTop: 24,
        fontSize: '15px',
        letterSpacing: '3px',
        color: '#E8EDF5',
        opacity: showSub ? 1 : 0,
        transform: showSub ? 'translateY(0)' : 'translateY(8px)',
        transition: 'opacity 0.8s ease, transform 0.8s ease',
      }}>동호회 교류전 매칭</p>
    </div>
  );
}

import { App as CapApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { Badge } from '@capawesome/capacitor-badge';

function AppInner() {
  const { user, authLoading, activeTeam } = useAuth();
  const [isAdminRoute, setIsAdminRoute] = useState(window.location.hash === "#admin");
  const [nav, setNav] = useState({ tab: "match", page: null, pageData: null });
  const [showSplash, setShowSplash] = useState(true);
  const [termsAgreed, setTermsAgreed] = useState(hasAgreedToTerms);
  const [blockedTeamIds, setBlockedTeamIds] = useState([]);
  const [blocksTick, setBlocksTick] = useState(0);
  const refetchBlocks = () => setBlocksTick((t) => t + 1);
  const [inviteToast, setInviteToast] = useState(null);
  const inviteProcessedRef = useRef(false);
  const profileCheckedRef = useRef(false);
  const [needProfileSetup, setNeedProfileSetup] = useState(
    localStorage.getItem("needProfileSetup") === "true"
  );
  const [showProfilePrompt, setShowProfilePrompt] = useState(false);

  // 닉네임 조회
  const { nickname, loading: nickLoading, refetch: refetchNick } = useNickname();

  // 채팅 미읽음 메시지 수 (탭 뱃지 + 방별 뱃지)
  const { unreadByRoom, totalUnread, markRoomAsRead } =
    useUnreadMessages(activeTeam?.team_name);

  // #admin 해시 변경 감지
  useEffect(() => {
    const handleHashChange = () => setIsAdminRoute(window.location.hash === "#admin");
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  // 프로필 완성 안내 팝업 체크
  useEffect(() => {
    if (
      user &&
      !needProfileSetup &&
      localStorage.getItem("hideProfilePrompt") !== "true"
    ) {
      setShowProfilePrompt(true);
    }
  }, [user, needProfileSetup]);

  // 앱 시작 시 버전 체크
  useEffect(() => {
    checkAppUpdate();

    // 앱 시작 시 아이콘 뱃지 초기화 (네이티브 환경에서만)
    (async () => {
      try {
        if (Capacitor.isNativePlatform()) {
          const supported = await Badge.isSupported();
          if (supported?.isSupported) {
            await Badge.clear();
          }
        }
      } catch (e) {
        console.error('뱃지 초기화 실패:', e);
      }
    })();
  }, []);

  // 차단 팀 목록 로드 (activeTeam 변경 또는 새 차단 발생 시 갱신)
  useEffect(() => {
    if (!activeTeam?.id) { setBlockedTeamIds([]); return; }
    supabase
      .from("blocks")
      .select("blocked_team_id")
      .eq("blocker_team_id", activeTeam.id)
      .then(({ data }) => {
        setBlockedTeamIds((data || []).map((r) => r.blocked_team_id));
      });
  }, [activeTeam?.id, blocksTick]);

  // 구글 로그인 완료 후 앱으로 돌아오는 딥링크 처리
  useEffect(() => {
    let unlistenApp;
    let unlistenBrowser;

    const setupListeners = async () => {
      unlistenApp = await CapApp.addListener('appUrlOpen', async (event) => {
        const url = event.url;
        if (url.startsWith('com.crewpick.android://') || url.startsWith('com.crewpick.app://')) {
          await Browser.close();

          const hashPart = url.split('#')[1] || '';
          const queryPart = url.split('?')[1]?.split('#')[0] || '';
          const params = new URLSearchParams(hashPart || queryPart);

          const accessToken = params.get('access_token');
          const refreshToken = params.get('refresh_token');
          const code = params.get('code');

          if (accessToken && refreshToken) {
            // 토큰 방식
            await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });
          } else if (code) {
            // PKCE 코드 방식
            await supabase.auth.exchangeCodeForSession(code);
          } else {
            // 파라미터 없을 때 잠시 후 세션 확인
            await new Promise(resolve => setTimeout(resolve, 1500));
            const { data } = await supabase.auth.getSession();
            if (!data?.session) {
              // 한 번 더 시도
              await new Promise(resolve => setTimeout(resolve, 1500));
              await supabase.auth.getSession();
            }
          }
        }
      });
    };

    setupListeners();

    return () => {
      unlistenBrowser?.then(l => l.remove());
      unlistenApp?.then(l => l.remove());
    };
  }, []);

  // Android 하드웨어 백버튼 처리
  const lastBackPressRef = useRef(0);
  useEffect(() => {
    let unlisten;
    (async () => {
      unlisten = await CapApp.addListener('backButton', () => {
        // 서브페이지가 열려있으면 뒤로가기
        if (nav.page) {
          setNav((prev) => ({ ...prev, page: null, pageData: null }));
          return;
        }
        // 메인 탭에서 두 번 누르면 앱 종료 (2초 이내)
        const now = Date.now();
        if (now - lastBackPressRef.current < 2000) {
          CapApp.exitApp();
        } else {
          lastBackPressRef.current = now;
          // 토스트 표시
          const toast = document.createElement('div');
          toast.textContent = '한 번 더 누르면 종료됩니다';
          toast.style.cssText = 'position:fixed;bottom:90px;left:50%;transform:translateX(-50%);background:rgba(0,0,0,0.8);color:#fff;padding:10px 24px;border-radius:20px;font-size:13px;z-index:9999;white-space:nowrap';
          document.body.appendChild(toast);
          setTimeout(() => toast.remove(), 2000);
        }
      });
    })();
    return () => { unlisten?.remove(); };
  }, [nav.page]);

  // 탭 전환 / 페이지 이동 시 스크롤 맨 위로 (채팅방 제외)
  useEffect(() => {
    if (nav.page !== "chatRoom") {
      window.scrollTo(0, 0);
    }
  }, [nav.tab, nav.page]);

  const navigate = (page, pageData = null) =>
    setNav((prev) => ({ ...prev, page, pageData }));

  const goBack = () =>
    setNav((prev) => ({ ...prev, page: null, pageData: null }));

  const switchTab = (tab) => {
    setNav({ tab, page: null, pageData: null });
  };

  // 스와이프 뒤로가기 제스처 (서브페이지에서만)
  const swipeStartRef = useRef(null);
  const handleSwipeStart = useCallback((e) => {
    const x = e.touches[0].clientX;
    // 화면 왼쪽 가장자리 30px 이내에서 시작해야 스와이프 인식
    if (x < 30) {
      swipeStartRef.current = { x, y: e.touches[0].clientY };
    }
  }, []);
  const handleSwipeEnd = useCallback((e) => {
    if (!swipeStartRef.current || !nav.page) { swipeStartRef.current = null; return; }
    const dx = e.changedTouches[0].clientX - swipeStartRef.current.x;
    const dy = Math.abs(e.changedTouches[0].clientY - swipeStartRef.current.y);
    swipeStartRef.current = null;
    // 가로 80px 이상, 세로 이동보다 가로가 클 때
    if (dx > 80 && dx > dy) {
      goBack();
    }
  }, [nav.page]);

  // 알림 클릭 → 화면 이동 이벤트 수신
  useEffect(() => {
    const handler = async (e) => {
      const { type, relatedId } = e.detail;

      if (type === "match_request") {
        setNav({ tab: "mymatches", page: null, pageData: { initialTab: "received" } });
      } else if (type === "match_accepted" || type === "match_rejected" || type === "match_cancelled") {
        setNav({ tab: "mymatches", page: null, pageData: { initialTab: "sent" } });
      } else if (type === "chat_message" && relatedId) {
        const { data: room } = await supabase
          .from("chat_rooms").select("*").eq("id", relatedId).maybeSingle();
        if (room) {
          setNav({ tab: "chat", page: "chatRoom", pageData: { room, myTeamName: activeTeam?.team_name } });
        } else {
          setNav({ tab: "chat", page: null, pageData: null });
        }
      }
    };

    window.addEventListener("notif-navigate", handler);
    return () => window.removeEventListener("notif-navigate", handler);
  }, [activeTeam]);

  // 로그인 직후 초대 링크 처리 (이메일 로그인은 LoginPage에서 저장, OAuth는 여기서 직접 처리)
  useEffect(() => {
    if (!user || inviteProcessedRef.current) return;
    inviteProcessedRef.current = true;

    const showToast = (msg) => {
      setInviteToast(msg);
      setTimeout(() => setInviteToast(null), 3000);
      if (msg.startsWith("🎉")) {
        localStorage.setItem("needProfileSetup", "true");
        setNeedProfileSetup(true);
      }
    };

    const stored = localStorage.getItem("inviteJoinResult");
    if (stored) {
      localStorage.removeItem("inviteJoinResult");
      showToast(stored);
      return;
    }

    const token = localStorage.getItem("pendingInviteToken");
    if (!token) return;
    localStorage.removeItem("pendingInviteToken");

    (async () => {
      const { data: invite } = await supabase
        .from("invite_codes").select("code")
        .eq("link_token", token).eq("is_active", true).maybeSingle();
      if (!invite?.code) { showToast("초대 링크가 만료됐거나 유효하지 않아요."); return; }
      const { data } = await supabase.rpc("use_invite_code", { p_code: invite.code, p_user_id: user.id });
      showToast(data?.success ? `🎉 ${data.team_name}팀에 가입됐어요!` : (data?.message || "초대 링크가 만료됐거나 유효하지 않아요."));
    })();
  }, [user]);

  // 신규 가입자 감지: user_profiles 없으면 프로필 설정 화면으로 유도
  useEffect(() => {
    if (!user || profileCheckedRef.current) return;
    if (localStorage.getItem("needProfileSetup") === "true") {
      profileCheckedRef.current = true;
      return;
    }
    profileCheckedRef.current = true;

    supabase.from("user_profiles").select("id").eq("id", user.id).maybeSingle()
      .then(({ data }) => {
        if (!data) {
          localStorage.setItem("needProfileSetup", "true");
          setNeedProfileSetup(true);
        }
      });
  }, [user]);

  // 푸시 알림 권한 요청 + FCM 토큰 device_tokens 저장
  useEffect(() => {
    if (!user) return;
    if (!Capacitor.isNativePlatform()) return;

    const platform = Capacitor.getPlatform();

    // registration 리스너: 토큰 수신 → device_tokens 저장
    PushNotifications.addListener('registration', async ({ value: token }) => {
      try {
        const { error } = await supabase
          .from('device_tokens')
          .upsert(
            {
              user_id: user.id,
              token,
              platform,
              updated_at: new Date().toISOString(),
            },
            { onConflict: 'token' }
          );
        if (error) {
          console.error('토큰 저장 에러:', error);
        }
      } catch (e) {
        console.error('토큰 저장 예외:', e);
      }
    });

    // registrationError 리스너
    PushNotifications.addListener('registrationError', (err) => {
      console.error('푸시 등록 에러:', err);
    });

    // 포그라운드 수신
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('푸시 수신(포그라운드):', notification);
    });

    const registerPush = async () => {
      try {
        let perm = await PushNotifications.checkPermissions();

        if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
          perm = await PushNotifications.requestPermissions();
        }

        if (perm.receive !== 'granted') {
          return;
        }

        await PushNotifications.register();
      } catch (e) {
        console.error('register 예외:', e);
      }
    };

    registerPush();

    return () => {
      PushNotifications.removeAllListeners();
    };
  }, [user]);

  if (isAdminRoute) {
    return (
      <Suspense fallback={<div style={{ padding: 40, textAlign: "center", color: "#666" }}>관리자 페이지 불러오는 중...</div>}>
        <AdminPage user={user} />
      </Suspense>
    );
  }

  if (showSplash) {
    return <SplashScreen onDone={() => setShowSplash(false)} />;
  }

  if (authLoading) {
    return (
      <div style={styles.loadingWrap}>
        <div style={styles.spinner} />
      </div>
    );
  }

  if (!user) {
    return (
      <div style={styles.appWrapper}>
        <LoginPage />
      </div>
    );
  }

  if (!termsAgreed) {
    return (
      <div style={styles.appWrapper}>
        <TermsAgreement onAgree={() => setTermsAgreed(true)} />
      </div>
    );
  }

  if (needProfileSetup) {
    return (
      <div style={styles.appWrapper}>
        <ProfileSetupPage onDone={() => {
          localStorage.removeItem("needProfileSetup");
          setNeedProfileSetup(false);
          refetchNick();
        }} />
      </div>
    );
  }

  // 로그인 유저의 닉네임이 없으면 강제 설정 모달 (ProfileSetupPage 중엔 건너뜀)
  const showNicknameModal = user && !nickLoading && !nickname && !needProfileSetup;

  const renderContent = () => {
    if (nav.page === "teamDetail") {
      // _fromPage가 있으면 그 페이지로 돌아가고, 없으면 탭 루트로 돌아감
      const handleTeamDetailBack = nav.pageData?._fromPage
        ? () => navigate(nav.pageData._fromPage)
        : goBack;
      return (
        <TeamDetail
          teamId={nav.pageData.teamId}
          onBack={handleTeamDetailBack}
          onMatchesClick={(tId) => {
            if (nav.pageData._team) {
              navigate("teamProfile", nav.pageData._team);
            }
          }}
        />
      );
    }
    if (nav.page === "teamProfile") {
      return (
        <TeamProfile
          team={nav.pageData}
          onBack={goBack}
          onLoginRequired={() => navigate("login")}
        />
      );
    }
    if (nav.page === "postCreate") {
      return (
        <PostCreate
          onBack={goBack}
          onSaved={() => setNav({ tab: "match", page: null, pageData: null })}
        />
      );
    }
    if (nav.page === "postEdit") {
      return (
        <PostCreate
          editPost={nav.pageData}
          onBack={goBack}
          onSaved={(updatedPost) =>
            setNav({ tab: "match", page: "postDetail", pageData: updatedPost })
          }
        />
      );
    }
    if (nav.page === "postDetail") {
      return (
        <PostDetail
          post={nav.pageData}
          onBack={goBack}
          onEditPost={(post) => navigate("postEdit", post)}
          onDeleted={() => setNav({ tab: "match", page: null, pageData: null })}
          onBlock={refetchBlocks}
        />
      );
    }
    if (nav.page === "chatRoom") {
      return (
        <ChatRoom
          room={nav.pageData.room}
          myTeamName={nav.pageData.myTeamName}
          onBack={goBack}
          onMarkRead={() => markRoomAsRead(nav.pageData.room.id)}
        />
      );
    }
    if (nav.page === "teamRegister") {
      return (
        <TeamRegister
          onBack={goBack}
          onSaved={() => setNav({ tab: "register", page: null, pageData: null })}
        />
      );
    }
    if (nav.page === "editTeam") {
      return (
        <TeamRegister
          editTeam={nav.pageData}
          onBack={goBack}
          onSaved={() => setNav({ tab: "register", page: null, pageData: null })}
        />
      );
    }
    if (nav.page === "board") {
      return <Board onHome={goBack} initialTab={nav.pageData?.initialTab} />;
    }
    if (nav.page === "guide") {
      return <UserGuide onBack={goBack} />;
    }
    if (nav.page === "privacy") {
      return <PrivacyPolicy onBack={goBack} />;
    }
    if (nav.page === "support") {
      return <Support onBack={goBack} />;
    }
    if (nav.page === "myProfile") {
      return (
        <MyProfilePage
          onBack={goBack}
          onProfileEdit={() => {
            localStorage.setItem("needProfileSetup", "true");
            setNeedProfileSetup(true);
          }}
        />
      );
    }
    if (nav.page === "myMatchEvents") {
      return <MyMatchEvents onBack={goBack} onEventClick={(data) => navigate("matchEventDetail", data)} />;
    }
    if (nav.page === "matchEventDetail") {
      const { application, post, hostTeam, myTeam } = nav.pageData || {};
      return <MatchEventDetail application={application} post={post} hostTeam={hostTeam} myTeam={myTeam} onBack={goBack} />;
    }
    if (nav.page === "teamSearch") {
      return (
        <MatchList
          key="matchlist-teamsearch"
          initialInnerTab="teams"
          hideInnerTabs
          onBack={goBack}
          pageTitle="팀 찾기"
          onTeamClick={(team) => navigate("teamDetail", { teamId: team.id, _team: team, _fromPage: "teamSearch" })}
          onPostClick={(post) => navigate("postDetail", post)}
          onCreatePost={() => navigate("postCreate")}
          onEditPost={(post) => navigate("postEdit", post)}
          onHome={goBack}
          blockedTeamIds={blockedTeamIds}
        />
      );
    }
    if (nav.page === "clubRecruitment") {
      return (
        <MatchList
          key="matchlist-club"
          initialInnerTab="club"
          hideInnerTabs
          onBack={goBack}
          pageTitle="회원 모집"
          onTeamClick={(team) => navigate("teamDetail", { teamId: team.id, _team: team, _fromPage: "clubRecruitment" })}
          onPostClick={(post) => navigate("postDetail", post)}
          onCreatePost={() => navigate("postCreate")}
          onEditPost={(post) => navigate("postEdit", post)}
          onHome={goBack}
          blockedTeamIds={blockedTeamIds}
        />
      );
    }

    switch (nav.tab) {
      case "match":
        return (
          <MatchList
            key="matchlist-main"
            onTeamClick={(team) => navigate("teamDetail", { teamId: team.id, _team: team })}
            onPostClick={(post) => navigate("postDetail", post)}
            onCreatePost={() => navigate("postCreate")}
            onEditPost={(post) => navigate("postEdit", post)}
            onHome={() => switchTab("match")}
            blockedTeamIds={blockedTeamIds}
            onGuide={() => navigate("guide")}
          />
        );
      case "chat":
        return (
          <ChatList
            unreadByRoom={unreadByRoom}
            onRoomClick={(room) =>
              navigate("chatRoom", { room, myTeamName: activeTeam?.team_name })
            }
            onHome={() => switchTab("match")}
          />
        );
      case "mymatches":
        return (
          <MyMatches
            initialTab={nav.pageData?.initialTab}
            onNavigateToChat={(room) =>
              navigate("chatRoom", { room, myTeamName: activeTeam?.team_name })
            }
            onHome={() => switchTab("match")}
          />
        );
      case "register":
        return (
          <TeamManage
            onRegisterNew={() => navigate("teamRegister")}
            onEditTeam={(team) => navigate("editTeam", team)}
            onHome={() => switchTab("match")}
            onTeamSearch={() => navigate("teamSearch")}
            onClubRecruitment={() => navigate("clubRecruitment")}
            onBoard={() => navigate("board")}
            onNotice={() => navigate("board", { initialTab: "notice" })}
            onInquiry={() => navigate("board", { initialTab: "문의" })}
            onPrivacy={() => navigate("privacy")}
            onSupport={() => navigate("support")}
            onProfileEdit={() => {
              localStorage.setItem("needProfileSetup", "true");
              setNeedProfileSetup(true);
            }}
            onViewProfile={() => navigate("myProfile")}
            onMyMatchEvents={() => navigate("myMatchEvents")}
            onGuide={() => navigate("guide")}
          />
        );
      default:
        return <MatchList onTeamClick={(team) => navigate("teamProfile", team)} blockedTeamIds={blockedTeamIds} />;
    }
  };

  return (
    <div style={styles.appWrapper}>
      {inviteToast && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999,
          background: inviteToast.startsWith("🎉") ? "#E8412A" : "#1f2937",
          color: "#fff", textAlign: "center",
          padding: "calc(env(safe-area-inset-top) + 14px) 24px 14px",
          fontSize: 14, fontWeight: 700,
        }}>
          {inviteToast}
        </div>
      )}
      {showNicknameModal && (
        <NicknameModal userId={user.id} onSaved={() => refetchNick()} />
      )}
      {showProfilePrompt && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 9000,
          background: "rgba(0,0,0,0.5)",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: "0 20px",
        }}>
          <div style={{
            background: "#fff", borderRadius: 20, maxWidth: 340, width: "100%",
            padding: "28px 24px 20px", textAlign: "center",
            boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
          }}>
            <div style={{ fontSize: 40, marginBottom: 14 }}>📋</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#1A1A2E", marginBottom: 10 }}>
              개인 프로필을 완성하세요
            </div>
            <div style={{ fontSize: 14, color: "#6b7280", lineHeight: 1.6, marginBottom: 24 }}>
              프로필을 작성하면 교류전 매칭이 더 쉬워져요. 닉네임과 종목 레벨을 등록해보세요!
            </div>
            <button
              onClick={() => {
                setShowProfilePrompt(false);
                localStorage.setItem("needProfileSetup", "true");
                setNeedProfileSetup(true);
              }}
              style={{
                width: "100%", padding: "13px 0", borderRadius: 12, border: "none",
                background: "#E8412A", color: "#fff", fontSize: 15, fontWeight: 700,
                cursor: "pointer", marginBottom: 10,
              }}
            >
              프로필 완성하러 가기
            </button>
            <button
              onClick={() => setShowProfilePrompt(false)}
              style={{
                width: "100%", padding: "12px 0", borderRadius: 12,
                background: "none", border: "1.5px solid #e5e7eb",
                color: "#6b7280", fontSize: 14, fontWeight: 600,
                cursor: "pointer", marginBottom: 10,
              }}
            >
              닫기
            </button>
            <button
              onClick={() => {
                localStorage.setItem("hideProfilePrompt", "true");
                setShowProfilePrompt(false);
              }}
              style={{
                background: "none", border: "none", color: "#9ca3af",
                fontSize: 12, cursor: "pointer", padding: "4px 0",
              }}
            >
              다시 보지 않기
            </button>
          </div>
        </div>
      )}
      <div style={styles.content} onTouchStart={handleSwipeStart} onTouchEnd={handleSwipeEnd}>
        {renderContent()}
      </div>

      <nav style={styles.tabBar}>
        <div style={styles.tabBarInner}>
          {TABS.map((tab) => {
            const active = nav.tab === tab.key;
            const isActivePage = active && !nav.page;
            // 채팅 탭에만 미읽음 뱃지 표시
            const badgeCount = tab.key === "chat" ? totalUnread : 0;
            return (
              <button
                key={tab.key}
                style={styles.tabBtn}
                onClick={() => switchTab(tab.key)}
              >
                <div style={{
                  ...styles.tabIconWrap,
                  background: isActivePage ? "rgba(226,75,74,0.10)" : "transparent",
                }}>
                  {tab.icon(isActivePage)}
                  {badgeCount > 0 && (
                    <span style={styles.tabBadge}>
                      {badgeCount > 99 ? "99+" : badgeCount}
                    </span>
                  )}
                </div>
                <span style={{
                  ...styles.tabLabel,
                  color: isActivePage ? "#E24B4A" : "#aaa",
                  fontWeight: isActivePage ? 700 : 400,
                }}>
                  {tab.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

const isKakaoInApp = () => /KAKAOTALK/i.test(navigator.userAgent);

function KakaoBlockScreen() {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: '#ffffff',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', zIndex: 9999,
      padding: '0 32px', textAlign: 'center',
    }}>
      <img src={splashLogo} alt="CrewPick" style={{ width: 80, marginBottom: 24 }} />
      <p style={{ fontSize: 20, fontWeight: 700, color: '#E24B4A', margin: '0 0 12px', lineHeight: 1.4 }}>
        크루픽은 외부 브라우저에서<br/>이용해주세요
      </p>
      <p style={{ fontSize: 14, color: '#888', margin: 0, lineHeight: 1.6 }}>
        우측 하단 <span style={{ fontWeight: 700, color: '#555' }}>···</span> → <span style={{ fontWeight: 700, color: '#555' }}>외부 브라우저로 열기</span>를 눌러주세요
      </p>
      <div style={{ width: 40, height: 2, background: '#E24B4A', borderRadius: 2, margin: '24px 0' }} />
      <p style={{ fontSize: 12, color: '#bbb', margin: 0 }}>카카오톡 인앱 브라우저는 지원하지 않습니다</p>
    </div>
  );
}

export default function App() {
  if (isKakaoInApp()) return <KakaoBlockScreen />;
  if (window.location.pathname === '/join') return <JoinPage />;

  return (
    <AuthProvider>
      <NotificationProvider>
        <NotificationToast />
        <AppInner />
      </NotificationProvider>
    </AuthProvider>
  );
}

const styles = {
  appWrapper: {
    position: "relative",
    minHeight: "100vh",
    background: "#f7f8fa",
    fontFamily: "'Noto Sans KR', sans-serif",
  },
  content: { paddingBottom: 72 },
  loadingWrap: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f7f8fa",
  },
  spinner: {
    width: 36,
    height: 36,
    border: "3px solid #edf0f3",
    borderTop: "3px solid #E24B4A",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },
  tabBar: {
    position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 100,
    background: "#ffffff", borderTop: "1px solid #edf0f3",
    boxShadow: "0 -2px 16px rgba(0,0,0,0.05)",
  },
  tabBarInner: {
    display: "flex", maxWidth: 430, margin: "0 auto",
    padding: "2px 0 calc(4px + env(safe-area-inset-bottom))",
  },
  tabBtn: {
    flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
    gap: 2, padding: "6px 0 8px", background: "none", border: "none",
    cursor: "pointer", position: "relative",
  },
  tabIconWrap: {
    position: "relative",
    width: 42, height: 36, borderRadius: 12,
    display: "flex", alignItems: "center", justifyContent: "center",
    transition: "background 0.15s",
  },
  tabBadge: {
    position: "absolute", top: 2, right: 2,
    background: "#E24B4A", color: "#fff",
    fontSize: 9, fontWeight: 700, borderRadius: 999,
    minWidth: 16, height: 16, padding: "0 3px",
    display: "flex", alignItems: "center", justifyContent: "center",
    border: "2px solid #fff",
  },
  tabLabel: { fontSize: 11, transition: "color 0.15s", letterSpacing: "-0.2px" },
};
